/**
 * Concrete Variation — procedural cast-concrete shading.
 *
 * `matConcrete` was a flat `MeshStandardMaterial` with no maps at all, so
 * under the key light every plinth collapsed to one valör and read as a
 * white PVC pipe rather than a cast pier. Everything around it (wood,
 * ground, cladding) is textured or shader-varied, which made the flat
 * surface stand out even more.
 *
 * Three signals, all world-space and asset-free:
 *   - blotches (~0.5 m)  — cement/water-ratio mottling across the pour
 *   - aggregate (~15 mm) — fine ballast speckle, brightness only
 *   - roughness          — wetter/dryer patches read differently at grazing
 *
 * Plus a **ground-contact darkening**: real piers have soil splash-back and
 * a damp band at the bottom. Without it the plinth meets the grass on a
 * hard bright edge that no amount of AO fixes (N8AO does not reach into a
 * contact zone this thin).
 *
 * ### Anchoring the contact band
 *
 * The band must sit on the pier's **foot**, and it cannot be derived from
 * world Y alone: plinth bottoms sit at different heights because
 * `visibleHeightM` tracks the terrain slope, so an absolute-Y band drifts
 * off the contact zone on any sloped site.
 *
 * The mesh origin alone is not enough either. Piers are origin-centred and
 * their height varies a lot — 0.4 m for the default square/barrel plinth
 * (`plinthLengthMm 500` − `minBuriedDepthMm 100`), 0.35 m for deck piers,
 * less on sloped ground. A band measured downward from the origin
 * saturates and then stays saturated all the way to the foot, so a 0.4 m
 * pier ends up with its whole lower third soil-tinted instead of a narrow
 * damp band.
 *
 * So the foot position is supplied per-geometry via the
 * `aConcreteSpanY` attribute (see `setConcreteContactSpan`) and the fade
 * is measured **upward from the foot**. Geometry without the attribute
 * reads 0 — the WebGL default for a disabled attribute array — which
 * disables the contact term rather than guessing a foot.
 *
 * Symmetric with woodVariation.ts / groundVariation.ts: same fail-fast
 * `replaceShaderChunkOrThrow` guard, same `customProgramCacheKey` chaining.
 */
import * as THREE from "three";

interface ConcreteVariationParams {
	/** Period (m) of the cement blotching. Default: 0.55 */
	blotchPeriodMeters?: number;
	/** Brightness amplitude of the blotching (0..1). Default: 0.11 */
	blotchStrength?: number;
	/** Cells per metre for the fine aggregate speckle. Default: 62 */
	aggregateFrequency?: number;
	/** Brightness amplitude of the aggregate speckle (0..1). Default: 0.085 */
	aggregateStrength?: number;
	/** Roughness modulation amplitude. Default: 0.14 */
	roughnessRange?: number;
	/**
	 * Height (m) **above the pier's foot** over which the ground-contact
	 * darkening fades out. Roughly what real soil splash-back covers, and
	 * bounded at both ends: full strength at the foot, nothing above this
	 * height, whatever the pier's height. Default: 0.08
	 */
	contactFadeHeight?: number;
	/** Maximum darkening at the foot (0..1). Default: 0.62 */
	contactStrength?: number;
	/** Damp-soil tone the contact zone tints toward. Default: 0x4a4335 */
	contactColor?: number;
	/** Extra roughness added at full contact strength. Default: 0.04 */
	contactRoughness?: number;
	/**
	 * Darkening of the rain streaks: water running down the shaft leaves
	 * narrow vertical tracks where dirt settles. 0 disables. Default: 0.16
	 */
	streakStrength?: number;
}

const DEFAULT_PARAMS: Required<ConcreteVariationParams> = {
	blotchPeriodMeters: 0.55,
	blotchStrength: 0.13,
	aggregateFrequency: 62,
	aggregateStrength: 0.085,
	roughnessRange: 0.14,
	contactFadeHeight: 0.12,
	contactStrength: 0.68,
	contactColor: 0x3f3a2f,
	contactRoughness: 0.06,
	streakStrength: 0.16,
};

/**
 * Vertex attribute carrying the pier's own height along its local Y, in
 * metres. `applyConcreteVariation` uses it to place the ground-contact
 * band on the foot instead of guessing from the mesh origin.
 */
export const CONCRETE_CONTACT_SPAN_ATTRIBUTE = "aConcreteSpanY";

/**
 * Tag a pier geometry with its own height so the contact band lands on
 * its foot. Call once per pier geometry, right after constructing it.
 *
 * Geometry that skips this reads 0 (the WebGL constant for a disabled
 * attribute array) and simply renders without ground soiling — a missing
 * band is a far cheaper failure than a band in the wrong place.
 */
export function setConcreteContactSpan(
	geometry: THREE.BufferGeometry,
	heightM: number,
): void {
	const vertexCount = geometry.getAttribute("position").count;
	const spans = new Float32Array(vertexCount).fill(heightM);
	geometry.setAttribute(
		CONCRETE_CONTACT_SPAN_ATTRIBUTE,
		new THREE.BufferAttribute(spans, 1),
	);
}

type ShaderStage = "vertex" | "fragment";

function replaceShaderChunkOrThrow(
	shaderSource: string,
	chunk: string,
	replacement: string,
	stage: ShaderStage,
): string {
	if (!shaderSource.includes(chunk)) {
		throw new Error(
			`[concreteVariation] Missing ${stage} shader chunk "${chunk}". ` +
				"Three.js shader layout changed; update applyConcreteVariation injection points.",
		);
	}

	return shaderSource.replace(chunk, replacement);
}

const VARIATION_GLSL = /* glsl */ `
  float concreteHash(vec2 co) {
    return fract(sin(dot(co, vec2(27.619, 57.583))) * 31718.927);
  }

  // Smoothed value noise, period 1 in input units. Caller scales coords.
  float concreteValueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = concreteHash(i);
    float b = concreteHash(i + vec2(1.0, 0.0));
    float c = concreteHash(i + vec2(0.0, 1.0));
    float d = concreteHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  // Plinths are vertical cylinders: sampling only XZ would smear the
  // noise into vertical streaks down the shaft. Fold Y in so the
  // pattern varies along the height too.
  vec2 concreteSamplePos(vec3 worldPos) {
    return vec2(worldPos.x + worldPos.z * 0.63, worldPos.y + worldPos.z * 0.37);
  }
`;

/**
 * Apply procedural cast-concrete shading to a MeshStandardMaterial.
 *
 * No maps required — diffuse variation and roughness are computed in the
 * fragment shader. Chains onto any existing `onBeforeCompile` and
 * `customProgramCacheKey` so it composes with `applyEdgeBevel`.
 */
export function applyConcreteVariation(
	material: THREE.MeshStandardMaterial,
	params: ConcreteVariationParams = {},
): void {
	const p = { ...DEFAULT_PARAMS, ...params };
	const blotchFreq = 1.0 / p.blotchPeriodMeters;
	const contactColor = new THREE.Color(p.contactColor);
	const existingHook = material.onBeforeCompile.bind(material);

	// See edgeBevel.ts: the parameter values are baked into the GLSL source
	// and are therefore invisible to Three's program cache. Without a key
	// contribution two concrete materials with different params would
	// silently share whichever variant compiled first.
	const existingCacheKey = material.customProgramCacheKey?.bind(material);
	const ourKey =
		`concrete-variation:${p.blotchPeriodMeters.toFixed(4)}:${p.blotchStrength.toFixed(4)}:` +
		`${p.aggregateFrequency.toFixed(2)}:${p.aggregateStrength.toFixed(4)}:` +
		`${p.roughnessRange.toFixed(4)}:${p.contactFadeHeight.toFixed(4)}:` +
		`${p.contactStrength.toFixed(4)}:${p.contactColor.toString(16)}:` +
		`${p.contactRoughness.toFixed(4)}:${p.streakStrength.toFixed(4)}`;
	material.customProgramCacheKey = () =>
		existingCacheKey ? `${existingCacheKey()}|${ourKey}` : ourKey;

	material.onBeforeCompile = (shader, renderer) => {
		existingHook?.(shader, renderer);

		// ── Vertex: world position + offset from the mesh's own origin ──
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <common>",
			`#include <common>
attribute float ${CONCRETE_CONTACT_SPAN_ATTRIBUTE};
varying vec3 vConcreteWorldPos;
varying float vConcreteAboveFootY;
varying float vConcreteHasFoot;`,
			"vertex",
		);
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <worldpos_vertex>",
			`#include <worldpos_vertex>
vConcreteWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
// The pier is origin-centred, so its foot is half its span down the
// mesh's local +Y axis. modelMatrix[1].y is that axis's world-Y
// component (scale and rotation included), so this stays correct for
// scaled meshes and does not assume the pier is axis-aligned.
float footDropY = ${CONCRETE_CONTACT_SPAN_ATTRIBUTE} * 0.5 * modelMatrix[1].y;
vConcreteAboveFootY =
  (vConcreteWorldPos.y - modelMatrix[3].y) + footDropY;
// Untagged geometry reads 0 from the disabled attribute array. Gate on
// it so those meshes lose the band entirely instead of anchoring it on
// the mesh origin and smearing dirt up the shaft.
vConcreteHasFoot = step(0.0001, ${CONCRETE_CONTACT_SPAN_ATTRIBUTE});`,
			"vertex",
		);

		// ── Fragment: declarations + helpers ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <common>",
			`#include <common>
varying vec3 vConcreteWorldPos;
varying float vConcreteAboveFootY;
varying float vConcreteHasFoot;
${VARIATION_GLSL}`,
			"fragment",
		);

		// ── Fragment: blotching, aggregate speckle, contact soiling ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <color_fragment>",
			`#include <color_fragment>
{
  vec2 cp = concreteSamplePos(vConcreteWorldPos);

  // Cement mottling — broad patches where the mix cured lighter/darker.
  float nBlotch = concreteValueNoise(cp * ${blotchFreq.toFixed(5)});
  float nBlotchFine = concreteValueNoise(cp * ${(blotchFreq * 3.17).toFixed(5)} + vec2(23.7, 61.3));
  float blotch = mix(nBlotch, nBlotchFine, 0.35) - 0.5;
  diffuseColor.rgb *= 1.0 + blotch * ${(p.blotchStrength * 2.0).toFixed(4)};

  // Aggregate speckle — luminance only, so the grey never shifts hue.
  float nAggregate = concreteValueNoise(cp * ${p.aggregateFrequency.toFixed(3)} + vec2(9.1, 44.7));
  diffuseColor.rgb *= 1.0 + (nAggregate - 0.5) * ${p.aggregateStrength.toFixed(4)};

  // Ground contact — damp soil splash-back at the foot of the pier.
  // Measured UPWARD from the foot, so the band is the same height on a
  // 0.4 m plinth and a 0.12 m one. Broken up by noise so it never reads
  // as a painted stripe.
  float contactRaw = (1.0 - smoothstep(
    0.0,
    ${p.contactFadeHeight.toFixed(4)},
    vConcreteAboveFootY
  )) * vConcreteHasFoot;
  float contactNoise = 0.72 + concreteValueNoise(cp * ${(blotchFreq * 6.3).toFixed(5)} + vec2(88.2, 12.9)) * 0.56;
  float contact = clamp(contactRaw * contactNoise, 0.0, 1.0) * ${p.contactStrength.toFixed(4)};
  vec3 soil = vec3(${contactColor.r.toFixed(4)}, ${contactColor.g.toFixed(4)}, ${contactColor.b.toFixed(4)});
  diffuseColor.rgb = mix(diffuseColor.rgb, soil, contact);

  // Rain streaks — noise stretched ~12x along Y, so each cell becomes a
  // narrow vertical track. Thresholded so only some tracks darken, and
  // strongest toward the foot where the run-off has collected the most.
  vec2 streakPos = vec2(
    (vConcreteWorldPos.x + vConcreteWorldPos.z) * 38.0,
    vConcreteWorldPos.y * 3.1
  );
  float streak = smoothstep(0.58, 0.86, concreteValueNoise(streakPos + vec2(5.3, 17.9)));
  float streakFoot = mix(0.45, 1.0, 1.0 - smoothstep(0.0, 0.45, vConcreteAboveFootY) * vConcreteHasFoot);
  diffuseColor.rgb *= 1.0 - streak * streakFoot * ${p.streakStrength.toFixed(4)};

  diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);
}
`,
			"fragment",
		);

		// ── Fragment: roughness from the same blotch octave ──
		// Damp/soiled concrete is rougher than the cured face, so the
		// contact zone also loses its specular sheen.
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <roughnessmap_fragment>",
			`#include <roughnessmap_fragment>
{
  vec2 cp = concreteSamplePos(vConcreteWorldPos);
  float n = concreteValueNoise(cp * ${blotchFreq.toFixed(5)});
  float contactRaw = (1.0 - smoothstep(
    0.0,
    ${p.contactFadeHeight.toFixed(4)},
    vConcreteAboveFootY
  )) * vConcreteHasFoot;
  roughnessFactor = clamp(
    roughnessFactor
      + (n - 0.5) * ${p.roughnessRange.toFixed(4)}
      + contactRaw * ${p.contactRoughness.toFixed(4)},
    0.0,
    1.0
  );
}
`,
			"fragment",
		);
	};

	material.needsUpdate = true;
}
